import type { CityConfig } from "../city-template/types";

export const tracyConfig: CityConfig = {
  city: "Tracy",
  state: "California",
  stateAbbr: "CA",
  slug: "tracy",
  route: "/cities/tracy",
  title: "Emergency Passport Tracy, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Tracy, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for San Joaquin County and Central Valley travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Tracy, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Tracy and San Joaquin County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Tracy%20California%20at%20golden%20hour%20with%20modern%20suburban%20downtown%20and%20Central%20Valley%20farmland%2C%20historic%20main%20street%20with%20charming%20storefronts%2C%20San%20Joaquin%20County%20landscape%20with%20agricultural%20fields%20and%20rolling%20hills%20in%20background%2C%20warm%20sunset%20light%20over%20the%20Central%20Valley%2C%20professional%20aerial%20photography%2C%20clean%20suburban%20California%20environment%2C%20no%20visible%20text%20or%20people%2C%20warm%20amber%20and%20green%20tones&width=1800&height=700&seq=tracy-hero-001&orientation=landscape",
  heroImageAlt: "Tracy California at golden hour with downtown, farmland, and Central Valley landscape",
  geoPosition: "37.7397;-121.4252",
  geoPlacename: "Tracy, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "450 Golden Gate Ave, San Francisco, CA 94102",
    distance: "Located in San Francisco — approximately 60–75 minutes from Tracy via I-580",
  },
  nearbyCitiesZips: [
    { name: "Tracy", zips: "95304, 95376, 95377, 95378, 95391" },
    { name: "Manteca", zips: "95336, 95337" },
    { name: "Lathrop", zips: "95330" },
    { name: "Stockton", zips: "95201–95213, 95215, 95219, 95267, 95269, 95296, 95297" },
    { name: "Lodi", zips: "95240, 95241, 95242" },
    { name: "Modesto", zips: "95350, 95351, 95354, 95355, 95356, 95357, 95358" },
    { name: "Ripon", zips: "95366" },
    { name: "Escalon", zips: "95320" },
    { name: "Livermore", zips: "94550, 94551" },
    { name: "Pleasanton", zips: "94566, 94588" },
    { name: "Dublin", zips: "94568, 94582" },
    { name: "Brentwood", zips: "94513" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent San Joaquin County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Stockton", route: "/cities/stockton" },
    { name: "Modesto", route: "/cities/modesto" },
    { name: "Sacramento", route: "/cities/sacramento" },
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Livermore", route: "/cities/livermore" },
    { name: "Oakland", route: "/cities/oakland" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Tracy remotely via secure courier. We do not have a walk-in office in Tracy — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for San Joaquin County travelers.",
  fpcOfficeDistance: "Serving all of San Joaquin County",
  serviceAreaIntro: "From the downtown corridor to the new developments, from the historic district to the growing suburbs — Fast Passport Center serves the entire Tracy community. We regularly help travelers from Manteca, Lathrop, Stockton, and Lodi get their passports fast.",
  nearbyCitiesIntro: "We serve all Tracy neighborhoods and nearby San Joaquin County and Central Valley cities including Manteca, Lathrop, Stockton, Lodi, Modesto, and Ripon. If you are in San Joaquin County or the northern Central Valley, we have you covered.",
  howItWorksIntro: "Most Tracy travelers complete everything online and ship documents overnight. No need to drive to San Francisco or Sacramento — we handle everything remotely.",
  mistakesIntro: "With SFO and OAK serving as the Bay Area's primary international airports, Tracy travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Tracy and San Joaquin County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Tracy? Here are the most common ones we hear from San Joaquin County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent San Joaquin County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Tom G.",
      location: "Tracy, CA",
      rating: 5,
      text: "My business trip to Germany was in 2 days and my passport was expired. Fast Passport Center got me a new one in 30 hours. Absolutely saved my trip.",
      service: "Emergency Passport",
    },
    {
      name: "Lisa M.",
      location: "Manteca, CA",
      rating: 5,
      text: "Needed to renew for a family cruise to Mexico. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "James R.",
      location: "Stockton, CA",
      rating: 5,
      text: "Lost my passport a week before my honeymoon to Jamaica. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Nancy K.",
      location: "Lodi, CA",
      rating: 5,
      text: "As a frequent international traveler for business, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "tr-1",
      name: "Tracy Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "835 W 11th St",
      city: "Tracy",
      state: "CA",
      zip: "95376",
      phone: "(209) 832-2104",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Tracy",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "tr-2",
      name: "Manteca Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "455 E Yosemite Ave",
      city: "Manteca",
      state: "CA",
      zip: "95336",
      phone: "(209) 823-4811",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.5 miles from downtown Manteca",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "tr-3",
      name: "Stockton Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "424 N El Dorado St",
      city: "Stockton",
      state: "CA",
      zip: "95202",
      phone: "(209) 466-5301",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from downtown Stockton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for San Joaquin County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "tr-4",
      name: "Lodi Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 W Lodi Ave",
      city: "Lodi",
      state: "CA",
      zip: "95240",
      phone: "(209) 369-0384",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Lodi",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern San Joaquin County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "tr-5",
      name: "Modesto Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1032 11th St",
      city: "Modesto",
      state: "CA",
      zip: "95354",
      phone: "(209) 527-0201",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Modesto",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Stanislaus County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "tr-6",
      name: "San Joaquin County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "102 S San Joaquin St",
      city: "Stockton",
      state: "CA",
      zip: "95202",
      phone: "(209) 468-3939",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "25 miles from Tracy",
      website: "https://www.sjgov.org/",
      websiteLabel: "San Joaquin County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "San Joaquin County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Central Valley acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Tracy applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many San Joaquin County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Tracy travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Tracy." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Tracy address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Tracy before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Tracy for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Central Valley travelers." },
    { q: "How fast can I renew my passport in Tracy?", a: "Fast Passport Center offers fast passport renewal in Tracy in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my trip?", a: "If your passport expires before your trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Tracy in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Tracy?", a: "Same-day passport appointments require a drive to the SF Regional Passport Agency. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Tracy residents." },
    { q: "Can I expedite a child passport in Tracy?", a: "Yes. Fast Passport Center expedites child passports in Tracy for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Tracy with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Tracy?",
      a: "With Fast Passport Center's emergency courier service, Tracy travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Tracy?",
      a: "Popular options include the Tracy Post Office (95376), Manteca Post Office (95336), Stockton Main Post Office (95202), and Lodi Post Office (95240). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Tracy?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in San Joaquin County or the northern Central Valley?",
      a: "We serve all of San Joaquin County including Manteca, Lathrop, Stockton, Lodi, Modesto, and Ripon. Our overnight shipping labels work from any UPS location, so your location within the Central Valley doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Tracy?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Tracy?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Tracy and Manteca offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Tracy?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Tracy?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk San Joaquin County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Tracy", address: "2550 N Naglee Rd, Tracy, CA 95304", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Manteca", address: "1200 E Yosemite Ave, Manteca, CA 95336", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Stockton", address: "3550 Pacific Ave, Stockton, CA 95204", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Lodi", address: "1030 W Lodi Ave, Lodi, CA 95240", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Modesto", address: "3400 Dale Rd, Modesto, CA 95356", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Stockton", address: "2233 Grand Canal Blvd, Stockton, CA 95207", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Tracy", address: "2600 S Tracy Blvd, Tracy, CA 95376", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Stockton", address: "1616 E Hammer Ln, Stockton, CA 95210", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Tracy",
      address: "3450 Naglee Rd, Tracy, CA 95304",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Stockton",
      address: "55 S Lincoln St, Stockton, CA 95203",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Modesto",
      address: "1240 E Prosperity Ave, Modesto, CA 95356",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Manteca",
      address: "955 E Yosemite Ave, Manteca, CA 95336",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Express services for San Joaquin County residents. Appointment required.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "55 miles from Tracy",
      address: "San Francisco, CA 94128",
      note: "The Bay Area's primary international airport. Accessible via I-580 W to I-80 W to US-101 N.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: true,
    },
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "45 miles from Tracy",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "International flights to Mexico and domestic connections. Accessible via I-580 W.",
      flightUrl: "https://www.oaklandairport.com/",
      flightLabel: "Oakland Airport",
      featured: false,
    },
    {
      code: "SAC",
      name: "Sacramento International Airport",
      distance: "50 miles from Tracy",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "International flights to Mexico and Canada. Accessible via I-5 N.",
      flightUrl: "https://www.sacramento.aero/smf/",
      flightLabel: "Sacramento Airport",
      featured: false,
    },
  ],
};